import { useMemo } from 'react';
import type { Maybe } from 'yup';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

import { formatSpaceType } from '../components/Utils';

/**
 * Generates an array of randomly selected unique items from the input array.
 *
 * @param {string[]} items - The array of items to select from.
 * @param {number} count - The number of items to randomly select.
 * @return {string[]} The array of randomly selected items.
 */
const getRandomItems = (items: string[], count: number): string[] => {
  const selected = [];
  const itemsCopy = [...items];

  for (let i = 0; i < count; i += 1) {
    const randomIdx = Math.floor(
      (itemsCopy.length - selected.length) * Math.random(),
    );

    selected.push(itemsCopy[randomIdx]);
    itemsCopy.splice(randomIdx, 1);
  }

  return selected;
};

interface SuggestionsProps {
  limitTo?: number;
  onlyFirstLetterCapitalized?: boolean;
  projectName: string;
  spaceType: Maybe<string> | undefined;
}

export const useSuggestions = ({
  limitTo,
  onlyFirstLetterCapitalized,
  projectName,
  spaceType = '',
}: SuggestionsProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const formattedSpaceType = formatSpaceType(spaceType as string);

  const operationSuggestions: string[] = useMemo(
    () =>
      t('customize.newProject.operationSuggestions', {
        returnObjects: true,
      }) || [],
    [t],
  );

  const spaceTypeSuggestions: string[] = useMemo(
    () =>
      t('customize.newProject.spaceTypeSuggestions', {
        returnObjects: true,
      }) || [],
    [t],
  );

  const projectNameSuggestions: string[] = useMemo(() => {
    const capitalSpaceType = formattedSpaceType
      ? formattedSpaceType[0].toUpperCase() + formattedSpaceType.slice(1)
      : '';
    let filteredSDpaceTypes: string[] = [];

    if (projectName) {
      // Check if the project name is an exact match
      // if it is, return it
      const withExactName = spaceTypeSuggestions.find(
        item => item.toLowerCase() === projectName.toLowerCase(),
      );
      filteredSDpaceTypes = withExactName
        ? [withExactName]
        : spaceTypeSuggestions.filter(item =>
            item.toLowerCase().startsWith(projectName.toLowerCase()),
          );

      if (filteredSDpaceTypes.length > 1) return filteredSDpaceTypes;
    }

    // check if the project already has an operation
    // if it does, we don't need suggestions
    if (
      operationSuggestions.some(item =>
        projectName.toLowerCase().endsWith(item.toLowerCase()),
      )
    )
      return [];

    return operationSuggestions.map(
      operation =>
        `${
          filteredSDpaceTypes[0] || projectName || capitalSpaceType
        } ${operation}`,
    );
  }, [
    operationSuggestions,
    projectName,
    formattedSpaceType,
    spaceTypeSuggestions,
  ]);

  const limitedProjectNameSuggestions = useMemo(
    () =>
      limitTo && projectNameSuggestions.length > limitTo
        ? getRandomItems(projectNameSuggestions, limitTo)
        : projectNameSuggestions,
    [limitTo, projectNameSuggestions],
  );

  const result = useMemo(
    () =>
      onlyFirstLetterCapitalized
        ? limitedProjectNameSuggestions.map(
            item =>
              item.toLowerCase().charAt(0).toUpperCase() +
              item.toLowerCase().slice(1),
          )
        : limitedProjectNameSuggestions,
    [limitedProjectNameSuggestions, onlyFirstLetterCapitalized],
  );

  return result;
};
